// Lookout's REST routes behind the RudderStack accept page (ACT2-1124 LLD §5.3). They are Hono routes, not oRPC,
// and need no session. The token and the password go only in the request body.
import { z } from 'zod';

const InviteCheckSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.enum(['PASSWORD_SIGNUP', 'ACCEPTED', 'SSO_SIGNUP']),
    email: z.string(),
    invitedBy: z.string().nullable(),
  }),
  z.object({ status: z.literal('INVALID'), message: z.string() }),
]);

export type RudderstackInviteCheck = z.infer<typeof InviteCheckSchema>;

const ErrorSchema = z.object({ message: z.string() }).loose();

const UNREACHABLE = 'Could not reach Lookout. Check your connection and try again.';
const FAILED = 'Something went wrong. Please try again.';

async function post(path: string, body: unknown): Promise<{ status: number; payload: unknown }> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error(UNREACHABLE);
  }
  return { status: response.status, payload: await response.json().catch(() => null) };
}

function errorOf(payload: unknown): Error {
  const parsed = ErrorSchema.safeParse(payload);
  return new Error(parsed.success ? parsed.data.message : FAILED);
}

/** RudderStack's status for the emailed token. An unknown or expired token is `INVALID`, with RudderStack's text. */
export async function validateRudderstackInvite(token: string): Promise<RudderstackInviteCheck> {
  const { status, payload } = await post('/api/rudderstack-invite/validate', { token });
  if (status !== 200) throw errorOf(payload);
  const parsed = InviteCheckSchema.safeParse(payload);
  if (!parsed.success) throw new Error(FAILED);
  return parsed.data;
}

/** A new person's account, for `PASSWORD_SIGNUP`. RudderStack's refusal (the password rule) is the error message. */
export async function acceptRudderstackInvite(input: { token: string; name: string; password: string }): Promise<void> {
  const { status, payload } = await post('/api/rudderstack-invite/accept', input);
  if (status !== 200) throw errorOf(payload);
}
